import type {
  CapturedEvent,
  Providers,
  AddEventCallback,
  ContextuneSnapshot,
  SnapshotFormat,
  EventLogEntry,
  MarketingParamsBlock,
} from "./types.js";
import { extractEventsFromBatch } from "./translation.js";
import { patchFetch } from "./patches/patchFetch.js";
import { patchSendBeacon } from "./patches/patchSendBeacon.js";
import { patchXMLHTTPRequest } from "./patches/patchXhr.js";
import { captureMarketingParams } from "./engine/marketingParams.js";
import { readDeviceInfo } from "./engine/deviceInfo.js";
import { sanitiseForLLM, SANITISE_SELECTOR_LIMIT } from "./sanitise/forLLM.js";
import { redactProperties } from "./engine/eventLog.js";
import { formatSnapshot } from "./formatters/index.js";
import { startTrackingExtras, type TrackingExtrasOptions } from "./tracking/index.js";
import { initSession, type SessionHandle, type Persistence } from "./session/index.js";

const SOURCE_MAP = {
  ga4: 'googleanalytics',
  segment: 'segment',
  mixpanel: 'mixpanel',
} as const satisfies Record<string, Providers>;

export type ContextuneSource = keyof typeof SOURCE_MAP;

export interface ContextuneOptions {
  /** Omit to use direct-call mode: only track()/page() calls are captured,
   *  no network interception. Useful when you want Contextune itself as the
   *  "analytics" layer. */
  source?: ContextuneSource;
  eventLogSize?: number;
  /** Controls event log lifetime. 'navigation' (default): cleared on page reload.
   *  'ct-session': survives reloads; scoped to 30 min of inactivity via cookie. */
  persistence?: Persistence;
  context?: {
    extras?: {
      /** Capture UTM params, referrer, and landing page. Default: true. */
      marketing?: boolean;
      /** Capture viewport, language, platform, and device type. Default: true. */
      device?: boolean;
    };
  };
  tracking?: TrackingExtrasOptions;
}

/**
 * Controls which events trigger a subscription handler.
 * - `'event'` — every entry added to the log
 * - predicate function — only entries for which the function returns `true`
 */
export type SubscriptionFilter =
  | 'event'
  | ((entry: EventLogEntry) => boolean);

/**
 * Handler called when a matching event enters the log.
 * `entry` is the new event; `state` is the full snapshot captured immediately
 * before the entry was added (the log does not yet contain `entry`).
 */
export type SubscriptionHandler = (
  entry: EventLogEntry,
  state: ContextuneSnapshot,
) => void;

export interface ContextuneInstance {
  getSnapshot(): ContextuneSnapshot;
  getSnapshot(options: { format: 'js' }): ContextuneSnapshot;
  getSnapshot(options: { format: 'json' | 'toon' }): string;
  track(name: string, properties?: Record<string, unknown>): void;
  page(url?: string): void;
  subscribe(filter: SubscriptionFilter, handler: SubscriptionHandler): () => void;
  clear(): void;
  destroy(): void;
}

const DEFAULT_EVENT_LOG_SIZE = 50;

type Listener = { filter: SubscriptionFilter; handler: SubscriptionHandler };

class ContextuneImpl implements ContextuneInstance {
  private readonly provider: Providers | null;
  private readonly capacity: number;
  private readonly sessionStartMs: number;
  private readonly session: SessionHandle;
  private readonly disposers: Array<() => void> = [];
  private readonly buffer: EventLogEntry[] = [];
  private readonly marketingParams: MarketingParamsBlock | null;
  private readonly enableDevice: boolean;
  private readonly listeners = new Set<Listener>();
  private destroyed = false;

  constructor(options: ContextuneOptions) {
    this.provider = options.source ? SOURCE_MAP[options.source] : null;
    this.capacity = Math.max(1, options.eventLogSize ?? DEFAULT_EVENT_LOG_SIZE);

    this.session = initSession(options.persistence);
    this.sessionStartMs = this.session.sessionStartMs;

    const persisted = this.session.loadEvents();
    const start = Math.max(0, persisted.length - this.capacity);
    for (let i = start; i < persisted.length; i++) {
      this.buffer.push(persisted[i]!);
    }

    const extras = options.context?.extras;
    const enableMarketing = extras?.marketing !== false;
    this.enableDevice = extras?.device !== false;
    this.marketingParams = enableMarketing ? captureMarketingParams() : null;

    if (typeof window === 'undefined') return;

    if (this.provider) {
      const callback: AddEventCallback = (event: CapturedEvent) => {
        this.handleCapture(event);
      };
      this.disposers.push(patchFetch(callback));
      this.disposers.push(patchSendBeacon(callback));
      this.disposers.push(patchXMLHTTPRequest(callback));
    }

    if (options.tracking) {
      this.disposers.push(startTrackingExtras(options.tracking, (name, props) => this.push(name, props)));
    }
  }

  private buildSnapshot(): ContextuneSnapshot {
    const snap: ContextuneSnapshot = {
      event_log: this.buffer.map(e => ({ ...e })),
    };
    if (this.marketingParams) snap.marketing_params = { ...this.marketingParams };
    if (this.enableDevice) snap.device_info = readDeviceInfo();
    return snap;
  }

  private push(name: string, properties?: Record<string, unknown>): void {
    if (this.destroyed) return;
    const entry: EventLogEntry = {
      name: sanitiseForLLM(name, SANITISE_SELECTOR_LIMIT),
      elapsed_s: Math.max(0, Math.floor((Date.now() - this.sessionStartMs) / 1000)),
    };
    const redacted = redactProperties(properties);
    if (redacted && Object.keys(redacted).length > 0) entry.properties = redacted;

    if (this.listeners.size > 0) {
      const state = this.buildSnapshot();
      this.buffer.push(entry);
      if (this.buffer.length > this.capacity) this.buffer.shift();
      this.session.save(this.buffer);
      for (const { filter, handler } of this.listeners) {
        if (filter === 'event' || filter(entry)) {
          try { handler({ ...entry }, state); } catch { /* isolate bad callbacks */ }
        }
      }
    } else {
      this.buffer.push(entry);
      if (this.buffer.length > this.capacity) this.buffer.shift();
      this.session.save(this.buffer);
    }
  }

  private handleCapture(event: CapturedEvent): void {
    if (this.destroyed) return;
    if (!this.provider || event.provider !== this.provider) return;

    const extracted = extractEventsFromBatch({
      ...event,
      href: window.location.href,
      sentAt: new Date(),
    });

    for (const e of extracted) {
      this.push(e.eventName, e.properties);
    }
  }

  track(name: string, properties?: Record<string, unknown>): void {
    this.push(name, properties);
  }

  page(url?: string): void {
    this.push('page_view', url ? { url } : undefined);
  }

  subscribe(filter: SubscriptionFilter, handler: SubscriptionHandler): () => void {
    const listener: Listener = { filter, handler };
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  getSnapshot(): ContextuneSnapshot;
  getSnapshot(options: { format: 'js' }): ContextuneSnapshot;
  getSnapshot(options: { format: 'json' | 'toon' }): string;
  getSnapshot(options?: { format?: SnapshotFormat }): ContextuneSnapshot | string {
    const snap = this.buildSnapshot();
    const format = options?.format ?? 'js';
    if (format === 'js') return snap;
    return formatSnapshot(snap, format);
  }

  clear(): void {
    if (this.destroyed) return;
    // Reset the event log (in memory and its persisted copy) while keeping the
    // SDK live: transport patches, subscribers, marketing/device capture, and
    // the session clock (elapsed_s) all continue.
    this.buffer.length = 0;
    this.session.save(this.buffer);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const dispose of this.disposers) dispose();
    this.disposers.length = 0;
    // Full teardown: purges persisted state (localStorage + cookie for
    // ct-session) so the profile cannot be recovered by a later init().
    this.session.destroy();
    this.buffer.length = 0;
    this.listeners.clear();
  }
}

let singleton: ContextuneImpl | null = null;

export const Contextune = {
  init(options: ContextuneOptions = {}): ContextuneInstance {
    if (singleton) return singleton;
    singleton = new ContextuneImpl(options);
    return singleton;
  },
  getSnapshot(options?: { format?: SnapshotFormat }): ContextuneSnapshot | string | null {
    if (!singleton) return null;
    const format = options?.format;
    if (!format || format === 'js') return singleton.getSnapshot();
    return singleton.getSnapshot({ format });
  },
  track(name: string, properties?: Record<string, unknown>): void {
    singleton?.track(name, properties);
  },
  page(url?: string): void {
    singleton?.page(url);
  },
  subscribe(filter: SubscriptionFilter, handler: SubscriptionHandler): () => void {
    if (!singleton) return () => {};
    return singleton.subscribe(filter, handler);
  },
  clear(): void {
    singleton?.clear();
  },
  destroy(): void {
    if (!singleton) return;
    singleton.destroy();
    singleton = null;
  },
};
